import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Activity, AlertTriangle, ExternalLink, RotateCw } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const TIME_FILTERS = [
  { value: "24h", label: "Últimas 24h", hours: 24 },
  { value: "48h", label: "Últimas 48h", hours: 48 },
  { value: "7d", label: "Últimos 7 dias", hours: 24 * 7 },
] as const;

type TimeframeValue = (typeof TIME_FILTERS)[number]["value"];

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    console.error("Failed to format date", error);
    return value;
  }
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toLocaleString("pt-BR", {
    minimumFractionDigits: size < 10 && unitIndex > 0 ? 1 : 0,
    maximumFractionDigits: 1,
  })} ${units[unitIndex]}`;
}

function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function toStringOrNull(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  return false;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => toStringOrNull(item) ?? "")
      .filter((item): item is string => item.length > 0);
  }
  return [];
}

function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }

  return `${minutes}min`;
}

type UsageRecord = {
  id: string;
  created_at: string;
  provider: string;
  model: string | null;
  operation: string;
  input_modality: string | null;
  output_modality: string | null;
  tokens_prompt: number | null;
  tokens_completion: number | null;
  total_tokens: number | null;
  cost: number | null;
};

type AiUsageEvent = {
  id: string;
  created_at: string;
  event_type: string;
  tenant_id: string | null;
  metadata: Record<string, unknown> | null;
};

type UploadAnalytics = {
  acceptedCount: number;
  rejectedCount: number;
  totalAcceptedBytes: number;
  lastAcceptedAt: string | null;
  lastRejectedAt: string | null;
  topRejectionReasons: { reason: string; count: number }[];
  recentRejections: { id: string; createdAt: string; reason: string; detail: string | null }[];
};

type CleanupRunInfo = {
  id: string;
  createdAt: string;
  scope: "tenant" | "global";
  dryRun: boolean;
  maxAgeSeconds: number;
  found: number;
  deleted: number;
  failures: string[];
};

type CleanupAnalytics = {
  totalRuns: number;
  tenantRuns: number;
  dryRuns: number;
  totalFound: number;
  totalDeleted: number;
  lastTenantRun: CleanupRunInfo | null;
  lastGlobalRun: CleanupRunInfo | null;
  recentRuns: CleanupRunInfo[];
};

async function fetchUsageRecords(tenantId: string, hours: number) {
  const since = resolveSince(hours);

  const { data, error } = await supabase
    .from("tenant_ai_usage")
    .select(
      "id, created_at, provider, model, operation, input_modality, output_modality, tokens_prompt, tokens_completion, total_tokens, cost",
    )
    .eq("tenant_id", tenantId)
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(100);

  if (error) throw error;
  return (data ?? []) as UsageRecord[];
}

function createEmptyUploadAnalytics(): UploadAnalytics {
  return {
    acceptedCount: 0,
    rejectedCount: 0,
    totalAcceptedBytes: 0,
    lastAcceptedAt: null,
    lastRejectedAt: null,
    topRejectionReasons: [],
    recentRejections: [],
  };
}

async function fetchUploadAnalytics(tenantId: string, hours: number): Promise<UploadAnalytics> {
  const since = resolveSince(hours);

  const [acceptedRes, rejectedRes] = await Promise.all([
    supabase
      .from("ai_usage_events")
      .select("id, created_at, event_type, tenant_id, metadata")
      .eq("tenant_id", tenantId)
      .eq("event_type", "upload:accepted")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(100),
    supabase
      .from("ai_usage_events")
      .select("id, created_at, event_type, tenant_id, metadata")
      .eq("tenant_id", tenantId)
      .eq("event_type", "upload:rejected")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(100),
  ]);

  if (acceptedRes.error) throw acceptedRes.error;
  if (rejectedRes.error) throw rejectedRes.error;

  const events = ([...(acceptedRes.data ?? []), ...(rejectedRes.data ?? [])] as AiUsageEvent[])
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (events.length === 0) {
    return createEmptyUploadAnalytics();
  }

  const analytics = createEmptyUploadAnalytics();
  const rejectionReasons = new Map<string, number>();
  const recentRejections: UploadAnalytics["recentRejections"] = [];

  for (const event of events) {
    const metadata = (event.metadata ?? {}) as Record<string, unknown>;

    if (event.event_type === "upload:accepted") {
      analytics.acceptedCount += 1;
      analytics.totalAcceptedBytes += toNumber(metadata.size ?? metadata.bytes ?? 0);
      if (!analytics.lastAcceptedAt) {
        analytics.lastAcceptedAt = event.created_at;
      }
      continue;
    }

    if (event.event_type === "upload:rejected") {
      analytics.rejectedCount += 1;
      if (!analytics.lastRejectedAt) {
        analytics.lastRejectedAt = event.created_at;
      }

      const reason = toStringOrNull(metadata.reason) ?? "desconhecido";
      rejectionReasons.set(reason, (rejectionReasons.get(reason) ?? 0) + 1);

      if (recentRejections.length < 5) {
        const detail =
          toStringOrNull(metadata.message) ??
          toStringOrNull(metadata.mimeType) ??
          toStringOrNull(metadata.mediaType);

        recentRejections.push({
          id: event.id,
          createdAt: event.created_at,
          reason,
          detail,
        });
      }
    }
  }

  analytics.recentRejections = recentRejections;
  analytics.topRejectionReasons = Array.from(rejectionReasons.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([reason, count]) => ({ reason, count }));

  return analytics;
}

function createEmptyCleanupAnalytics(): CleanupAnalytics {
  return {
    totalRuns: 0,
    tenantRuns: 0,
    dryRuns: 0,
    totalFound: 0,
    totalDeleted: 0,
    lastTenantRun: null,
    lastGlobalRun: null,
    recentRuns: [],
  };
}

async function fetchCleanupAnalytics(tenantId: string, hours: number): Promise<CleanupAnalytics> {
  const since = resolveSince(hours);

  const [tenantRes, globalRes] = await Promise.all([
    supabase
      .from("ai_usage_events")
      .select("id, created_at, event_type, tenant_id, metadata")
      .eq("tenant_id", tenantId)
      .eq("event_type", "cleanup:run")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(100),
    supabase
      .from("ai_usage_events")
      .select("id, created_at, event_type, tenant_id, metadata")
      .is("tenant_id", null)
      .eq("event_type", "cleanup:run")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(100),
  ]);

  if (tenantRes.error) throw tenantRes.error;
  if (globalRes.error) throw globalRes.error;

  const events = ([...(tenantRes.data ?? []), ...(globalRes.data ?? [])] as AiUsageEvent[])
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (events.length === 0) {
    return createEmptyCleanupAnalytics();
  }

  const analytics = createEmptyCleanupAnalytics();
  const recentRuns: CleanupRunInfo[] = [];

  for (const event of events) {
    const metadata = (event.metadata ?? {}) as Record<string, unknown>;
    const scope = toStringOrNull(metadata.scope) === "tenant" ? "tenant" : "global";
    const dryRun = toBoolean(metadata.dryRun);
    const info: CleanupRunInfo = {
      id: event.id,
      createdAt: event.created_at,
      scope,
      dryRun,
      maxAgeSeconds: toNumber(metadata.maxAgeSeconds),
      found: toNumber(metadata.found),
      deleted: toNumber(metadata.deleted),
      failures: toStringArray(metadata.failures),
    };

    analytics.totalRuns += 1;
    analytics.totalFound += info.found;
    analytics.totalDeleted += info.deleted;
    if (dryRun) analytics.dryRuns += 1;

    if (scope === "tenant") {
      analytics.tenantRuns += 1;
      if (!analytics.lastTenantRun) {
        analytics.lastTenantRun = info;
      }
    } else if (!analytics.lastGlobalRun) {
      analytics.lastGlobalRun = info;
    }

    if (recentRuns.length < 5) {
      recentRuns.push(info);
    }
  }

  analytics.recentRuns = recentRuns;
  return analytics;
}

function resolveSince(hours: number) {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

async function fetchStats(tenantId: string, hours: number): Promise<any> {
  const since = resolveSince(hours);

  const [pendingRes, errorRes, processedRes, generationReqRes, assetsRes] = await Promise.all([
    supabase
      .from("ai_index_queue")
      .select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId)
      .is("processed_at", null),
    supabase
      .from("ai_index_queue")
      .select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId)
      .not("error", "is", null),
    supabase
      .from("ai_index_queue")
      .select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId)
      .not("processed_at", "is", null)
      .gte("processed_at", since),
    supabase
      .from("ai_generation_requests")
      .select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId)
      .gte("created_at", since),
    supabase
      .from("ai_generation_assets")
      .select("id, ai_generation_requests!inner(tenant_id)", { count: "exact", head: true })
      .eq("ai_generation_requests.tenant_id", tenantId)
      .gte("created_at", since),
  ]);

  if (pendingRes.error) throw pendingRes.error;
  if (errorRes.error) throw errorRes.error;
  if (processedRes.error) throw processedRes.error;
  if (generationReqRes.error) throw generationReqRes.error;
  if (assetsRes.error) throw assetsRes.error;

  return {
    pending: pendingRes.count ?? 0,
    errored: errorRes.count ?? 0,
    processed24h: processedRes.count ?? 0,
    generationCount: generationReqRes.count ?? 0,
    assetsCount: assetsRes.count ?? 0,
    usageTokens: 0,
    usageCostCents: 0,
  };
}

async function fetchRecentErrors(tenantId: string, hours: number) {
  const since = resolveSince(hours);

  const { data, error } = await supabase
    .from("ai_index_queue")
    .select("id, entity_type, operation, queued_at, processed_at, error")
    .eq("tenant_id", tenantId)
    .not("error", "is", null)
    .gte("processed_at", since)
    .order("processed_at", { ascending: false })
    .limit(20);

  if (error) throw error;
  return data ?? [];
}

export function ObservabilityTab() {
  const { tenantId, isAdminSaas } = useTenant();
  const queryClient = useQueryClient();
  const [timeframe, setTimeframe] = useState<TimeframeValue>("24h");
  const [reprocessLimit, setReprocessLimit] = useState<number>(5);

  const selectedFilter = useMemo(
    () => TIME_FILTERS.find((filter) => filter.value === timeframe) ?? TIME_FILTERS[0],
    [timeframe],
  );

  const {
    data: stats,
    error: statsError,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useQuery({
    queryKey: ["ai-observability-stats", tenantId, timeframe],
    enabled: Boolean(tenantId && !isAdminSaas),
    queryFn: () => fetchStats(tenantId!, selectedFilter.hours),
  });

  const {
    data: failures,
    error: failuresError,
    isLoading: isFailuresLoading,
    isFetching: isFailuresFetching,
  } = useQuery({
    queryKey: ["ai-observability-failures", tenantId, timeframe],
    enabled: Boolean(tenantId && !isAdminSaas),
    queryFn: () => fetchRecentErrors(tenantId!, selectedFilter.hours),
  });

  const {
    data: usageRecords,
    error: usageError,
    isLoading: isUsageLoading,
    isFetching: isUsageFetching,
  } = useQuery({
    queryKey: ["ai-observability-usage", tenantId, timeframe],
    enabled: Boolean(tenantId && !isAdminSaas),
    queryFn: () => fetchUsageRecords(tenantId!, selectedFilter.hours),
  });

  const {
    data: uploadData,
    error: uploadError,
    isLoading: isUploadLoading,
    isFetching: isUploadFetching,
  } = useQuery({
    queryKey: ["ai-observability-uploads", tenantId, timeframe],
    enabled: Boolean(tenantId && !isAdminSaas),
    queryFn: () => fetchUploadAnalytics(tenantId!, selectedFilter.hours),
  });

  const {
    data: cleanupData,
    error: cleanupError,
    isLoading: isCleanupLoading,
    isFetching: isCleanupFetching,
  } = useQuery({
    queryKey: ["ai-observability-cleanup", tenantId, timeframe],
    enabled: Boolean(tenantId && !isAdminSaas),
    queryFn: () => fetchCleanupAnalytics(tenantId!, selectedFilter.hours),
  });

  const isLoading =
    isStatsLoading ||
    isFailuresLoading ||
    isUsageLoading ||
    isUploadLoading ||
    isCleanupLoading;

  const isRefreshing =
    isStatsFetching ||
    isFailuresFetching ||
    isUsageFetching ||
    isUploadFetching ||
    isCleanupFetching;

  const hasErrors = failures && failures.length > 0;

  const uploadAnalytics = uploadData ?? createEmptyUploadAnalytics();
  const cleanupAnalytics = cleanupData ?? createEmptyCleanupAnalytics();

  const metrics = useMemo(
    () => [
      {
        label: "Itens pendentes",
        value: stats?.pending ?? 0,
        description: "Aguardando processamento pela Edge Function",
      },
      {
        label: "Itens com erro",
        value: stats?.errored ?? 0,
        description: selectedFilter.label,
      },
      {
        label: `Processados (${selectedFilter.label})`,
        value: stats?.processed24h ?? 0,
        description: "Quantidade concluída no período selecionado",
      },
      {
        label: `Gerações (${selectedFilter.label})`,
        value: stats?.generationCount ?? 0,
        description: "Chamadas às funções síncronas de IA",
      },
      {
        label: `Assets criados (${selectedFilter.label})`,
        value: stats?.assetsCount ?? 0,
        description: "Outputs registrados em ai_generation_assets",
      },
      {
        label: "Tokens utilizados",
        value: stats ? stats.usageTokens.toLocaleString("pt-BR") : 0,
        description: "Total de tokens consumidos no período",
      },
      {
        label: "Custo estimado",
        value:
          stats && typeof stats.usageCostCents === "number"
            ? `R$ ${(stats.usageCostCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
            : "R$ 0,00",
        description: "Somatório de cost_amount_cents",
      },
      {
        label: `Uploads aceitos (${selectedFilter.label})`,
        value: uploadAnalytics.acceptedCount,
        description: "Uploads temporários aprovados",
      },
      {
        label: `Uploads rejeitados (${selectedFilter.label})`,
        value: uploadAnalytics.rejectedCount,
        description: "Total de rejeições registradas",
      },
      {
        label: "Bytes aceitos",
        value: formatBytes(uploadAnalytics.totalAcceptedBytes),
        description: "Volume agregado de uploads aceitos",
      },
      {
        label: `Limpezas executadas (${selectedFilter.label})`,
        value: cleanupAnalytics.totalRuns,
        description: "Chamadas registradas para limpeza",
      },
    ],
    [stats, selectedFilter.label, uploadAnalytics, cleanupAnalytics],
  );

  const usageSummary = useMemo(() => {
    const records = usageRecords ?? [];

    return records.reduce(
      (acc, record) => {
        const prompt = record.tokens_prompt ?? 0;
        const completion = record.tokens_completion ?? 0;
        const totalTokens = record.total_tokens ?? prompt + completion;

        return {
          count: acc.count + 1,
          tokensPrompt: acc.tokensPrompt + prompt,
          tokensCompletion: acc.tokensCompletion + completion,
          tokensTotal: acc.tokensTotal + totalTokens,
          cost: acc.cost + (record.cost ?? 0),
        };
      },
      { count: 0, tokensPrompt: 0, tokensCompletion: 0, tokensTotal: 0, cost: 0 },
    );
  }, [usageRecords]);

  const handleRefresh = useCallback(() => {
    if (!tenantId) return;

    queryClient.invalidateQueries({ queryKey: ["ai-observability-stats", tenantId, timeframe] });
    queryClient.invalidateQueries({ queryKey: ["ai-observability-failures", tenantId, timeframe] });
    queryClient.invalidateQueries({ queryKey: ["ai-observability-usage", tenantId, timeframe] });
    queryClient.invalidateQueries({ queryKey: ["ai-observability-uploads", tenantId, timeframe] });
    queryClient.invalidateQueries({ queryKey: ["ai-observability-cleanup", tenantId, timeframe] });
  }, [queryClient, tenantId, timeframe]);

  const reprocessMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) {
        throw new Error("Tenant não encontrado.");
      }

      const sanitizedLimit = Number.isFinite(reprocessLimit) ? Math.max(1, Math.min(reprocessLimit, 50)) : 5;
      const selectedIds = (failures ?? [])
        .slice(0, sanitizedLimit)
        .map((failure) => failure.id)
        .filter(Boolean);

      if (selectedIds.length === 0) {
        throw new Error("Nenhum item com erro encontrado no período selecionado.");
      }

      const { data, error } = await supabase.functions.invoke("ai-indexer-reprocess", {
        body: {
          tenantId,
          itemIds: selectedIds,
          limit: sanitizedLimit,
        },
      });

      if (error) {
        throw new Error(error.message ?? "Falha ao solicitar reprocessamento.");
      }

      return data;
    },
    onSuccess: (result: { reprocessedCount: number }) => {
      if (result.reprocessedCount > 0) {
        toast.success(`Reprocessamento solicitado para ${result.reprocessedCount} item(s).`);
      } else {
        toast.info("Nenhum item foi reprocessado.");
      }
      queryClient.invalidateQueries({ queryKey: ["ai-observability-stats", tenantId, timeframe] });
      queryClient.invalidateQueries({ queryKey: ["ai-observability-failures", tenantId, timeframe] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Não foi possível reprocessar os itens.");
    },
  });

  if (isAdminSaas) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Observabilidade indisponível</CardTitle>
          <CardDescription>
            O painel de monitoramento é exibido apenas para tenants específicos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Acesse como um tenant para visualizar métricas da fila e das Edge Functions.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Nenhum tenant selecionado</CardTitle>
          <CardDescription>
            Vincule-se a um tenant para acompanhar a fila de indexação.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Visão geral</h2>
            <p className="text-sm text-muted-foreground">
              Acompanhe a fila `ai_index_queue` e erros recentes das Edge Functions.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <a
                href="/docs/observability/ai-indexing"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1"
              >
                Playbook interno
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline">
              <a
                href="https://runtime.dev/docs/supabase-ai-indexer-observability"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1"
              >
                Documentação
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline">
              <a
                href="https://app.supabase.com/project/_/logs?ref=ai-indexer"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1"
              >
                Logs Supabase
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="timeframe" className="text-sm font-medium text-muted-foreground">
              Período
            </Label>
            <Select value={timeframe} onValueChange={(value: TimeframeValue) => setTimeframe(value)}>
              <SelectTrigger id="timeframe" className="w-[180px]">
                <SelectValue placeholder="Selecione o período" />
              </SelectTrigger>
              <SelectContent>
                {TIME_FILTERS.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RotateCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
              {isRefreshing ? "Atualizando..." : "Atualizar métricas"}
            </Button>
            <div className="flex items-center gap-2">
              <Label htmlFor="reprocess-limit" className="text-sm text-muted-foreground">
                Reprocessar últimos
              </Label>
              <Input
                id="reprocess-limit"
                type="number"
                min={1}
                max={50}
                value={reprocessLimit}
                onChange={(event) => setReprocessLimit(Number(event.target.value) || 1)}
                className="w-24"
              />
            </div>
            <Button
              type="button"
              onClick={() => reprocessMutation.mutate()}
              disabled={reprocessMutation.isPending || !hasErrors}
            >
              {reprocessMutation.isPending ? "Reprocessando..." : "Reprocessar"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {metrics.map((metric) => (
          <Card key={metric.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.label}</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">{metric.value}</div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{metric.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {(statsError || failuresError || usageError || uploadError || cleanupError) && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar métricas</AlertTitle>
          <AlertDescription>
            {(statsError ?? failuresError ?? usageError ?? uploadError ?? cleanupError)?.message
              ?? "Falha desconhecida ao consultar o Supabase."}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Uploads temporários</CardTitle>
            <CardDescription>Visão resumida das tentativas mais recentes.</CardDescription>
          </div>
          {isRefreshing && <span className="text-xs text-muted-foreground">Atualizando...</span>}
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="grid gap-3 md:grid-cols-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Último upload aceito</p>
                  <p className="text-base">
                    {uploadAnalytics.lastAcceptedAt ? formatDateTime(uploadAnalytics.lastAcceptedAt) : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Último upload rejeitado</p>
                  <p className="text-base">
                    {uploadAnalytics.lastRejectedAt ? formatDateTime(uploadAnalytics.lastRejectedAt) : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Principais motivos de rejeição</p>
                  {uploadAnalytics.topRejectionReasons.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma rejeição registrada no período.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {uploadAnalytics.topRejectionReasons.map((item) => (
                        <Badge key={item.reason} variant="outline">
                          {item.reason} · {item.count}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Rejeições recentes</p>
                {uploadAnalytics.recentRejections.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma rejeição registrada no período.</p>
                ) : (
                  <div className="space-y-2">
                    {uploadAnalytics.recentRejections.map((item) => (
                      <div key={item.id} className="rounded-lg border p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Badge variant="destructive">{item.reason}</Badge>
                          <span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                        </div>
                        {item.detail && (
                          <p className="mt-2 text-xs text-muted-foreground break-words">{item.detail}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Execuções de limpeza</CardTitle>
            <CardDescription>Resumo das últimas chamadas da função de limpeza.</CardDescription>
          </div>
          {isRefreshing && <span className="text-xs text-muted-foreground">Atualizando...</span>}
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="grid gap-3 md:grid-cols-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <p className="text-sm font-medium text-muted-foreground">Última execução do tenant</p>
                  {cleanupAnalytics.lastTenantRun ? (
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Quando</span>
                        <span>{formatDateTime(cleanupAnalytics.lastTenantRun.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Dry run</span>
                        <span>{cleanupAnalytics.lastTenantRun.dryRun ? "Sim" : "Não"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Encontrados</span>
                        <span>{cleanupAnalytics.lastTenantRun.found}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Removidos</span>
                        <span>{cleanupAnalytics.lastTenantRun.deleted}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">maxAge</span>
                        <span>{formatDuration(cleanupAnalytics.lastTenantRun.maxAgeSeconds)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">Nenhuma execução registrada para o tenant.</p>
                  )}
                </div>

                <div className="rounded-lg border p-3">
                  <p className="text-sm font-medium text-muted-foreground">Última execução global</p>
                  {cleanupAnalytics.lastGlobalRun ? (
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Quando</span>
                        <span>{formatDateTime(cleanupAnalytics.lastGlobalRun.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Dry run</span>
                        <span>{cleanupAnalytics.lastGlobalRun.dryRun ? "Sim" : "Não"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Encontrados</span>
                        <span>{cleanupAnalytics.lastGlobalRun.found}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Removidos</span>
                        <span>{cleanupAnalytics.lastGlobalRun.deleted}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">maxAge</span>
                        <span>{formatDuration(cleanupAnalytics.lastGlobalRun.maxAgeSeconds)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">Nenhuma execução global registrada.</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Execuções recentes</p>
                {cleanupAnalytics.recentRuns.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma execução recente.</p>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quando</TableHead>
                          <TableHead>Escopo</TableHead>
                          <TableHead>Dry run</TableHead>
                          <TableHead className="text-right">Encontrados</TableHead>
                          <TableHead className="text-right">Removidos</TableHead>
                          <TableHead className="text-right">Falhas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cleanupAnalytics.recentRuns.map((run) => (
                          <TableRow key={run.id}>
                            <TableCell>{formatDateTime(run.createdAt)}</TableCell>
                            <TableCell className="capitalize">{run.scope}</TableCell>
                            <TableCell>{run.dryRun ? "Sim" : "Não"}</TableCell>
                            <TableCell className="text-right">{run.found}</TableCell>
                            <TableCell className="text-right">{run.deleted}</TableCell>
                            <TableCell className="text-right">
                              {run.failures.length > 0 ? run.failures.length : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Falhas recentes</CardTitle>
            <CardDescription>Últimos 20 itens em erro na fila.</CardDescription>
          </div>
          {isRefreshing && <span className="text-xs text-muted-foreground">Atualizando...</span>}
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ) : !hasErrors ? (
            <p className="text-sm text-muted-foreground">Nenhuma falha registrada recentemente. 🎉</p>
          ) : (
            <div className="space-y-3">
              {failures?.map((failure) => (
                <div
                  key={failure.id}
                  className="rounded-lg border p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{failure.entity_type}</Badge>
                      <Badge variant="outline">{failure.operation}</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Enfileirado: {formatDateTime(failure.queued_at)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-destructive break-words">
                    {failure.error ?? "Erro desconhecido"}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Processado: {formatDateTime(failure.processed_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Consumo de IA (billing)</CardTitle>
            <CardDescription>
              Até 100 registros mais recentes no período. Tokens e custos refletem os lançamentos em `tenant_ai_usage`.
            </CardDescription>
          </div>
          {usageSummary.count > 0 && (
            <div className="text-xs text-muted-foreground text-right space-y-1">
              <div>
                Total de tokens: {usageSummary.tokensTotal.toLocaleString("pt-BR")}
                {usageSummary.tokensCompletion > 0 && (
                  <span>
                    {" "}
                    <span className="text-[11px]">(prompt {usageSummary.tokensPrompt.toLocaleString("pt-BR")}
                    {" "}
                    · conclusão {usageSummary.tokensCompletion.toLocaleString("pt-BR")})</span>
                  </span>
                )}
              </div>
              <div>
                Custo acumulado: R$
                {" "}
                {(usageSummary.cost).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Operação</TableHead>
                  <TableHead>Provider / Modelo</TableHead>
                  <TableHead>Modalidades</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Custo (R$)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    </TableRow>
                  ))
                ) : (usageRecords?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                      Nenhum consumo registrado no período selecionado.
                    </TableCell>
                  </TableRow>
                ) : (
                  usageRecords!.map((record) => {
                    const promptTokens = record.tokens_prompt ?? 0;
                    const completionTokens = record.tokens_completion ?? 0;
                    const totalTokens = record.total_tokens ?? promptTokens + completionTokens;

                    return (
                      <TableRow key={record.id}>
                        <TableCell>{formatDateTime(record.created_at)}</TableCell>
                        <TableCell className="capitalize">{record.operation.replace("-", " ")}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium uppercase tracking-wide text-xs text-muted-foreground">{record.provider}</span>
                            <span className="text-sm">{record.model ?? "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                            {record.input_modality && <Badge variant="outline">in: {record.input_modality}</Badge>}
                            {record.output_modality && <Badge variant="outline">out: {record.output_modality}</Badge>}
                            {!record.input_modality && !record.output_modality && <span>-</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-medium">{totalTokens.toLocaleString("pt-BR")}</span>
                            {(promptTokens > 0 || completionTokens > 0) && (
                              <span className="text-xs text-muted-foreground">
                                {promptTokens.toLocaleString("pt-BR")}
                                {completionTokens > 0 && ` · ${completionTokens.toLocaleString("pt-BR")}`}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {record.cost !== null && record.cost !== undefined
                            ? record.cost.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                            : "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            Valores em reais consideram a tabela padrão em `recordUsage`. Para tenants com `billing.mode = 'tenant_key'`, os débitos não são aplicados automaticamente.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
